import { Box, Collapse, Divider, Grid } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useCallback, useMemo, useState } from "react";
import type { ConditionFilterDescriptor } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ChartOfAccountsItem, ConditionField } from "../../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../../api/types/svTypes.ts";
import MetaIcon from "../../../../common/MetaIcon.tsx";
import ConditionFieldOptionPopup from "../../../../viewer/common/conditions/ConditionFieldOptionPopup.tsx";
import { formatCaption, getFormattedValue } from "../../../../viewer/common/conditions/conditionsHelper.tsx";
import useSelectedDictionaryRows from "../../../../viewer/common/conditions/useSelectedDictionaryRows.ts";
import { getFiltersToApply } from "../../../../viewer/common/hooks/getFiltersToApply.ts";
import { FilterFieldItem } from "../FilterFieldItem.tsx";
import CopyFilter from "./CopyFilter.tsx";
import FilterItem from "./FilterItem.tsx";

interface Props {
  condition: ConditionField;
  conditions: ConditionField[];
  editable: boolean;
  chartOfAccountItems?: ChartOfAccountsItem[];
  onChanged: (changes: Partial<ConditionField>) => void;
  onRemove: () => void;
}

export const Filter = ({ condition, conditions, editable, onChanged, onRemove, chartOfAccountItems }: Props) => {
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const { rows: selectedRows, isLoading } = useSelectedDictionaryRows(condition);
  const narrowingFilters = useMemo(() => getFiltersToApply(conditions, condition), [condition, conditions]);

  const handleFormatCaption = useCallback(
    (field: ConditionField) => {
      if (expanded) return undefined;
      return formatCaption(field);
    },
    [expanded]
  );

  const handleClosePopup = useCallback(() => {
    setOptionsAnchorEl(null);
  }, []);

  const copyElement = useMemo(() => {
    if (editable) return null;
    return (
      <CopyFilter
        visible={true}
        values={condition.config.filter?.values.map(getFormattedValue) || []}
        sx={{
          position: "relative",
          maxHeight: "30px",
        }}
      />
    );
  }, [editable, condition.config.filter?.values]);

  const onSave = useCallback(
    (filter: ConditionFilterDescriptor) => {
      if (!deepEqual(filter, condition.config.filter)) {
        onChanged({ config: { ...condition.config, filter } });
      }
      handleClosePopup();
    },
    [condition.config, onChanged, handleClosePopup]
  );

  return (
    <Box sx={{ flexDirection: "column", width: "100%" }}>
      <Grid container sx={{ position: "relative", width: "100%" }}>
        <FilterFieldItem
          field={condition}
          canBeRemoved={editable}
          hideOptions={!editable}
          formatCaption={handleFormatCaption}
          onShowOptions={(settings) => setOptionsAnchorEl(settings.ref)}
          isLoading={isLoading}
          startIcon={<MetaIcon icon={condition.meta.icon} />}
          expandable
          onToggle={setExpanded}
          disableExpanding={selectedRows.length === 0}
          copyButton={copyElement}
          onSetAutoShowItemOptionsId={undefined}
          readonly={false}
        />
        {optionsAnchorEl && (
          <ConditionFieldOptionPopup
            anchorEl={optionsAnchorEl}
            field={condition}
            save={onSave}
            onRemoveItem={onRemove}
            canBeRemoved={editable}
            resizable={condition.meta.type !== ItemDataType.Date}
            readonlyCustomLabel={true}
            disableEqualitySelection={true}
            narrowingFilters={narrowingFilters}
            CustomId={null}
          />
        )}
      </Grid>
      <Collapse orientation="vertical" in={expanded}>
        <Divider />
        <Grid container sx={{ position: "relative", paddingLeft: 3.25, paddingRight: 2, width: "100%" }}>
          {chartOfAccountItems !== undefined &&
            selectedRows.map((item) => (
              <FilterItem
                key={String(item[condition.meta.keyFieldName])}
                chartOfAccountItems={chartOfAccountItems}
                conditionName={condition.meta.name}
                text={item[condition.meta.availableFields[0] || ""]}
                name={item[condition.meta.availableFields[1] || ""]}
              />
            ))}
        </Grid>
      </Collapse>
    </Box>
  );
};

export default Filter;
